import { Fragment, FunctionComponent, ReactElement, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useParams, NavLink, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Button, Paper, Stack, Tab, Tabs } from '@mui/material';
import { DeleteOutlineRounded, EditRounded } from '@mui/icons-material';
import { useCustomer } from '@/queries/customers';
import { Roles } from '@/core/types';
import useAuth from '@/shared/context/auth-context';
import AdminPanelPage from '@/views/layouts/admin-panel/AdminPanelPage';
import Backdrop from '@/components/ui/backdrop';
import CustomerForm, { CustomerFormModel } from '@/features/customers/CustomerForm';
import DeleteCustomerForm from '@/features/customers/DeleteCustomerForm';
import { initialCustomerFormModel } from '@/features/customers/customer-form.defaults';
import { Customer } from '@/core/entities';

export type CustomerContext = { customer: Customer | null };

const CustomerPage: FunctionComponent = (): ReactElement => {
	const { user: authUser } = useAuth();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const location = useLocation();
	const { customerId } = useParams();
	const { queryKey: useCustomerQueryKey, query: useCustomerQuery } = useCustomer(customerId);
	const { data: customer } = useCustomerQuery;

	const [editCustomerForm, setEditCustomerForm] = useState<{
		open: boolean;
		customerId?: string;
		formModel: CustomerFormModel;
	}>({
		open: false,
		customerId,
		formModel: initialCustomerFormModel,
	});

	const [deleteCustomerForm, setDeleteCustomerForm] = useState<{ open: boolean; model?: Customer }>(
		{
			open: false,
		}
	);

	const refetch = () => {
		/* eslint-disable @typescript-eslint/no-floating-promises */
		queryClient.invalidateQueries(useCustomerQueryKey);
	};

	useEffect(() => {
		if (customer && editCustomerForm.open) {
			const { id, name, timeZone } = customer;
			setEditCustomerForm((editCustomerForm) => ({
				...editCustomerForm,
				customerId: id,
				formModel: { name, timeZone },
			}));
		}
	}, [customer, setEditCustomerForm, editCustomerForm.open]);

	const tabs = useMemo(() => {
		if (!customerId) return undefined;
		return {
			'site-management': {
				label: 'Site management',
				path: `/customers/${customerId}/site-management`,
			},
			devices: {
				label: 'Devices',
				path: `/customers/${customerId}/devices`,
			},
			users: {
				label: 'Users',
				path: `/customers/${customerId}/users`,
			},
		};
	}, [customerId]);

	const [currentTab, setCurrentTab] = useState(tabs?.['site-management'].path);

	useEffect(() => {
		if (customerId && location.pathname !== `/customers/${customerId}`)
			setCurrentTab(location.pathname);
	}, [location.pathname, customerId]);

	if (!customer) {
		return <Backdrop />;
	}

	const adminControls = (
		<Stack direction="row" spacing={1.5}>
			<Button
				variant="outlined"
				color="error"
				startIcon={<DeleteOutlineRounded />}
				onClick={() => setDeleteCustomerForm({ open: true, model: customer })}
			>
				Remove
			</Button>
			<Button
				variant="outlined"
				startIcon={<EditRounded />}
				onClick={() => setEditCustomerForm({ ...editCustomerForm, open: true })}
			>
				Edit
			</Button>
		</Stack>
	);

	return (
		<Fragment>
			<AdminPanelPage
				title={customer.name}
				breadcrumbs={[{ title: 'Customers', path: '/customers' }, { title: customer.name }]}
				headerControls={authUser?.role === Roles.LEVIANT_ADMIN ? adminControls : null}
			>
				<Stack direction="column">
					<Tabs value={currentTab}>
						{tabs &&
							Object.entries(tabs).map(([tabName, tab]) => (
								<Tab
									key={tabName}
									component={NavLink}
									label={tab.label}
									value={tab.path}
									to={tab.path}
								/>
							))}
					</Tabs>
					<Box component={Paper} padding={3}>
						<Outlet context={{ customer } satisfies CustomerContext} />
					</Box>
				</Stack>
			</AdminPanelPage>
			<CustomerForm
				open={editCustomerForm.open}
				onClose={() => setEditCustomerForm({ ...editCustomerForm, open: false })}
				formModel={editCustomerForm.formModel}
				updateFormModel={(field: keyof CustomerFormModel, value: string) =>
					setEditCustomerForm({
						...editCustomerForm,
						formModel: { ...editCustomerForm.formModel, [field]: value },
					})
				}
				customerId={editCustomerForm.customerId}
				onSubmitted={refetch}
			/>
			<DeleteCustomerForm
				open={deleteCustomerForm.open}
				onClose={() => setDeleteCustomerForm({ ...deleteCustomerForm, open: false })}
				customer={deleteCustomerForm.model}
				onSubmitted={() => navigate('/customers', { replace: true })}
			/>
		</Fragment>
	);
};

export default CustomerPage;
